import React, { useRef } from "react";
import { Form, Button } from "react-bootstrap";

interface ColorPickerProps {
  value: string | undefined;
  onChange: (color: string | undefined) => void;
  label?: string;
}

const PRESET_COLORS = [
  { name: "Red", value: "#D32F2F" },
  { name: "Blue", value: "#1976D2" },
  { name: "Green", value: "#388E3C" },
  { name: "Orange", value: "#F57C00" },
  { name: "Purple", value: "#7B1FA2" },
  { name: "Teal", value: "#00796B" },
  { name: "Pink", value: "#C2185B" },
  { name: "Indigo", value: "#303F9F" },
  { name: "Amber", value: "#FFA000" },
  { name: "Cyan", value: "#0097A7" },
];

/**
 * ColorPicker component - allows selecting from preset colors or choosing a custom color
 */
const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  label = "Team Color (optional)",
}) => {
  const customColorInputRef = useRef<HTMLInputElement>(null);

  const handlePresetClick = (color: string) => {
    onChange(color);
  };

  const handleCustomClick = () => {
    customColorInputRef.current?.click();
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleClear = () => {
    onChange(undefined);
  };

  const isCustomColor =
    value && !PRESET_COLORS.some((preset) => preset.value === value);

  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "center",
        }}
      >
        {PRESET_COLORS.map((color) => (
          <button
            key={color.value}
            type="button"
            onClick={() => handlePresetClick(color.value)}
            title={color.name}
            style={{
              width: "32px",
              height: "32px",
              backgroundColor: color.value,
              border:
                value === color.value
                  ? "3px solid var(--text-primary)"
                  : "1px solid var(--border-medium)",
              borderRadius: "4px",
              cursor: "pointer",
              padding: 0,
              position: "relative",
            }}
          >
            {value === color.value && (
              <span
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "#fff",
                  fontSize: "16px",
                  fontWeight: "bold",
                  textShadow: "0 0 2px rgba(0,0,0,0.8)",
                }}
              >
                ✓
              </span>
            )}
          </button>
        ))}

        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleCustomClick}
          style={{
            height: "32px",
            padding: "0 12px",
            display: "flex",
            alignItems: "center",
            border: isCustomColor ? "2px solid var(--text-primary)" : undefined,
          }}
        >
          {isCustomColor ? (
            <>
              <span
                style={{
                  display: "inline-block",
                  width: "16px",
                  height: "16px",
                  backgroundColor: value,
                  borderRadius: "2px",
                  marginRight: "6px",
                  border: "1px solid var(--border-medium)",
                }}
              />
              Custom
            </>
          ) : (
            "Custom"
          )}
        </Button>

        <input
          ref={customColorInputRef}
          type="color"
          value={value || "#000000"}
          onChange={handleCustomColorChange}
          style={{ display: "none" }}
        />

        {value && (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleClear}
            style={{ height: "32px", padding: "0 12px" }}
          >
            Clear
          </Button>
        )}
      </div>
      {value && (
        <Form.Text className="text-muted">Selected color: {value}</Form.Text>
      )}
    </Form.Group>
  );
};

export default ColorPicker;
